import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The keyed store an AnimationPlayer pushes sampled values through to the nodes that show them
 * (ADR-0016, ADR-0017). A value changes each frame, so it reaches a node through a listener, never
 * through React state that would render the scene again. The store keeps the last value of a key, so
 * a node that mounts mid-playback starts on it.
 */
import { createContext, useContext, useRef } from 'react';
function createPushStore() {
    /** Written only by `set`: the values written now. A release deletes the key. */
    const values = new Map();
    const listeners = new Map();
    return {
        set(key, value) {
            if (value === null)
                values.delete(key);
            else
                values.set(key, value);
            listeners.get(key)?.forEach((listener) => listener(value));
        },
        subscribe(key, listener) {
            const listening = listeners.get(key) ?? new Set();
            listening.add(listener);
            listeners.set(key, listening);
            const written = values.get(key);
            if (written !== undefined)
                listener(written);
            return () => {
                listening.delete(listener);
            };
        },
    };
}
const NO_STORE = {
    set: () => { },
    subscribe: () => () => { },
};
/** A provider and a reader for one kind of pushed value. With no provider, the reader gets a store that reaches no one. */
export function createPushStoreContext(displayName) {
    const Context = createContext(NO_STORE);
    Context.displayName = displayName;
    return {
        Provider: ({ children }) => {
            const store = useRef(null);
            store.current ??= createPushStore();
            return _jsx(Context.Provider, { value: store.current, children: children });
        },
        useStore: () => useContext(Context),
    };
}
//# sourceMappingURL=pushStore.js.map